import {
  type ChainType,
  deriveAllAddresses,
  walletGroupedAccounts,
} from '@polaris/core/models'
import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useSupportedChains } from '@/src/queries/hooks/chains'
import { cosmosDerivationPathsAtom } from '../hotkeys/atoms'
import { accountsAtom } from './atoms'

/** Derives addresses across all supported chains for a set of wallet grouped accounts. */
export function useAllDerivedAddresses(walletGroupingKey?: string) {
  const derivationPaths = useAtomValue(cosmosDerivationPathsAtom)
  const accounts = useAtomValue(accountsAtom)
  const groupedAccounts = useMemo(
    () => walletGroupedAccounts(accounts),
    [accounts],
  )
  const { data: supportedChains, isFetched: isChainsFetched } =
    useSupportedChains()

  const wallet = groupedAccounts.find(
    (group) => group.grouping_key === walletGroupingKey,
  )

  // Ensure if the connections change, the query is invalidated
  const walletEcosystems =
    wallet?.accounts.reduce(
      (acc, account) => acc.add(account.ecosystem),
      new Set<ChainType>(),
    ) ?? new Set<ChainType>()

  return useQuery({
    queryKey: ['derivedAddresses', walletGroupingKey, walletEcosystems.size],
    queryFn: () =>
      deriveAllAddresses(
        wallet?.accounts ?? [],
        supportedChains || [],
        derivationPaths,
      ),
    enabled: Boolean(wallet) && isChainsFetched && walletEcosystems.size > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}
